export class DeferredMedia extends HTMLElement {
  isPlaying = false;
  mediaPoster = null;
  toggleMediaButton = null;

  connectedCallback() {
    this.mediaPoster = this.querySelector('[data-ref="deferred-media-poster"]');
    this.toggleMediaButton = this.querySelector('[data-ref="toggle-media-button"]');

    this.mediaPoster?.addEventListener('click', this.showDeferredMedia.bind(this));
    this.toggleMediaButton?.addEventListener('click', this.toggleMedia.bind(this));

    this.observeAutoplayMedia();
  }

  disconnectedCallback() {
    this.autoplayObserver?.disconnect();
    this.autoplayObserver = null;
  }

  /**
   * Cellsius — exception n° 8 : une vidéo en lecture automatique n'est plus écrite en dur par
   * snippets/video.liquid (seulement dans <template>) ; on la monte quand elle arrive à un écran du
   * viewport, pour ne pas télécharger des Mo de vidéos que le visiteur ne voit pas encore.
   * Une vidéo déjà rendue (sans couverture, ou média prioritaire de la première section) est laissée telle quelle.
   */
  observeAutoplayMedia() {
    if (!this.hasAttribute('autoplay') || this.getAttribute('data-media-loaded')) return;

    const container = this.querySelector('[data-ref="deferred-media-video"]');
    if (!container || container.firstElementChild) return;
    if (!this.querySelector('template')?.content.firstElementChild) return;

    if (!('IntersectionObserver' in window)) {
      this.loadContent(false);
      return;
    }

    this.autoplayObserver?.disconnect();
    this.autoplayObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.autoplayObserver?.disconnect();
        this.autoplayObserver = null;
        this.loadContent(false);
      },
      { rootMargin: '150% 0px' }, // exception n° 8 bis : 1,5 écran d'avance (réseau lent)
    );
    this.autoplayObserver.observe(this);
  }

  updatePlayPauseHint(isPlaying) {
    if (this.toggleMediaButton instanceof HTMLElement) {
      this.toggleMediaButton.classList.remove('hidden');

      const playIcon = this.toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);

      const pauseIcon = this.toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');

    this.querySelector('[data-ref="deferred-media-video"]')?.appendChild(content);

    // this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.querySelector('[ref="deferred-media-poster"]')?.classList.add('deferred-media__playing');

    // Cellsius — exception n° 8 bis : la couverture d'une vidéo automatique ne disparaît qu'à la première image.
    if (this.hasAttribute('autoplay')) {
      const reveal = () => this.setAttribute('data-media-playing', '');
      if (content instanceof HTMLVideoElement) content.addEventListener('playing', reveal, { once: true });
      else reveal();
    }

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      content.play()?.catch(() => {});
    }
  }

  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*',
      );
    } else {
      this.querySelector('video')?.play();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*',
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}
